import { FC } from "react";
import {
  DateInput,
  Edit,
  EditProps,
  FormGroupContextProvider,
  FormWithRedirect,
  minValue,
  NumberInput,
  required,
  TextInput,
  useNotify,
  useRedirect,
  useUpdate,
} from "react-admin";
import dayjs from "dayjs";
import { Timestamp } from "firebase/firestore";

import CustomCard from "../../../../components/CustomCard";
import CustomToolbar from "../../../../components/CustomToolbar";
import FormWrapper from "../../../../components/FormWrapper";
import ImageUploadInput from "../../../../components/ImageUploadInput";
import Spacing from "../../../../components/Spacing";
import useCurrentUser from "../../../../hooks/useCurrentUser";
import useDisabledOnLoading from "../../../../hooks/useDisabledOnLoading";
import { firestore } from "../../../../init/firebase";
import { FormGroupNames } from "../../../../types/reactAdmin.types";
import { AppResource } from "../../../../types/resource.types";
import { isExists, validateFileSize } from "../../../../utils/validators";
import useServicesLocalizations from "./utils/useServicesLocalizations";
import { crossDateValidator } from "./utils/CrossDateValidator";
import { Box } from "@material-ui/core";

const CustomForm: FC<any> = (props) => {
  const service = props.record;
  const { user } = useCurrentUser();
  const redirect = useRedirect();
  const [update, { loading }] = useUpdate();
  const notify = useNotify();
  const notEditable = service.status === "completed" || service.status === "expired";

  const entertainmentsQuery = (key: string, value: string) =>
    firestore
      .collection(AppResource.entertainments)
      .doc(user.id)
      .collection(AppResource.services)
      .where(key, "==", value)
      .limit(1);

  const { disabledStyles } = useDisabledOnLoading(loading || notEditable);
  const localizations = useServicesLocalizations(props);

  const handleSubmit = (data: any) => {
    update(
      AppResource.entertainmentServices,
      props.record.id,
      {
        values: data,
        user,
      },
      service,
      {
        onSuccess() {
          notify("Service has been updated", "success");
          redirect(`/${AppResource.entertainmentServices}`);
        },
        onFailure(ex: any) {
          console.log(ex.message);
          notify("Error on updating service", "error");
        },
      }
    );
  };

  return (
    <Box>
      <FormWrapper>
        <FormGroupContextProvider name={FormGroupNames.serviceEdit}>
          <FormWithRedirect
            {...props}
            save={handleSubmit}
            render={(formProps) => {
              return (
                <form onSubmit={formProps.handleSubmit} style={disabledStyles}>
                  <CustomCard title="Details">
                    <Spacing>
                      {localizations?.[0]}
                      {localizations?.[1]}
                    </Spacing>

                    <Spacing>
                      {localizations?.[2]}
                      {localizations?.[3]}
                    </Spacing>

                    <Spacing>
                      <DateInput
                        source="from"
                        label="From"
                        format={(value: any) =>
                          value instanceof Timestamp ? dayjs(value.toDate()).format("YYYY-MM-DD") : value
                        }
                        fullWidth
                        variant="outlined"
                        validate={[required(), crossDateValidator("before", "to")]}
                      />
                      <DateInput
                        source="to"
                        label="To"
                        format={(value: any) =>
                          value instanceof Timestamp ? dayjs(value.toDate()).format("YYYY-MM-DD") : value
                        }
                        fullWidth
                        variant="outlined"
                        validate={[required(), crossDateValidator("after", "from")]}
                      />
                    </Spacing>

                    <Spacing>
                      <NumberInput
                        variant="outlined"
                        source="price"
                        style={{ width: "100%" }}
                        validate={[required(), minValue(0)]}
                      />

                      <TextInput
                        source="refNumber"
                        label="Reference Number"
                        variant="outlined"
                        fullWidth
                        validate={[
                          required(),
                          isExists({ query: entertainmentsQuery, type: "refNumber", currentValue: service.refNumber }),
                        ]}
                      />
                    </Spacing>

                    <NumberInput
                      variant="outlined"
                      source="maxRequests"
                      style={{ width: "100%" }}
                      validate={[minValue(0)]}
                    />
                  </CustomCard>

                  <CustomCard title="Images">
                    <ImageUploadInput multiple source="images" validate={[required(), validateFileSize("images")]} />
                  </CustomCard>

                  <CustomToolbar {...formProps} formName={FormGroupNames.serviceEdit} customErrors={{}} />
                </form>
              );
            }}
          />
        </FormGroupContextProvider>
      </FormWrapper>
    </Box>
  );
};

const ServiceEdit: FC<EditProps> = (props) => {
  return (
    <Edit {...props} component="section">
      <CustomForm />
    </Edit>
  );
};

export default ServiceEdit;
